import throttle from "lodash/throttle";

import { laptop, tablet } from "./breakpoints";
import "./polyfill";

// css for tiny slider
import "@/node_modules/tiny-slider/dist/tiny-slider.css";

// load blocks
import ImagesGallery from "@/blocks/ImagesGallery";
import "@/blocks/EntryText";
import "@/blocks/ContentBlock";
import "@/blocks/AdvertisingBanner";
import TitleAndAccordion from "@/blocks/TitleAndAccordion";

// load components
import Anchor from "@/components/Anchor";
import "@/components/Container";
import "@/components/Button";
import "@/components/PageTitle";
import "@/components/Map";
import "@/components/Accordion";
import CookieBanner from "@/components/CookieBanner";
import "@/components/Footer";
import Header from "@/components/Header";
import SampleForm from "@/components/SampleForm";
import ContactForm from "@/components/ContactForm";
import NewsletterForm from "@/components/NewsletterForm";
import "@/components/Row";
import "@/components/Col";
import DiscoverNewsEventsHeader from "@/components/DiscoverNewsEventsHeader";
import Sitemap from "@/components/Sitemap";
import MainNav from "@/components/MainNav";
import Hero from "@/components/Hero";
import NaturalCapital from "@/components/NaturalCapital";
import RegisterYourInterestCTA from "@/components/RegisterYourInterestCTA";
import OurPropertiesSlider from "@/components/OurPropertiesSlider";
import PageHero from "@/components/PageHero";
import NewsAndEvents from "@/components/NewsAndEvents";
import Label from "@/components/Label";
import Featured from "@/components/Featured";
import NewsAndEventsDetail from "@/components/NewsAndEventsDetail";
import Share from "@/components/Share";
import RelatedNews from "@/components/RelatedNews";
import ArticleItem from "@/components/ArticleItem";
import AboutPage from "@/components/AboutPage";
import StaticPage from "@/components/StaticPage";
import Dots from "@/components/Dots";
import Arrows from "@/components/Arrows";
import FaqPage from "@/components/FaqPage";
import ContactPage from "@/components/ContactPage";
import PeopleSlider from "@/components/PeopleSlider";
import ImagePlaceholder from "@/components/ImagePlaceholder";
import Offices from "@/components/Offices";
import Error404 from "@/components/Error404";
import HomeArticlesSlider from "@/components/HomeArticlesSlider";
import BookValuationForm from "@/components/BookValuationForm";
import ServicesTabs from "@/components/ServicesTabs";
import OurPropertiesListPage from "@/components/OurPropertiesListPage";
import OurPropertiesList from "@/components/OurPropertiesList";
import OurPropertiesListMap from "@/components/OurPropertiesListMap";
import PropertyPreview from "@/components/PropertyPreview";
import RequestInfoForm from "@/components/RequestInfoForm";
import PropertyPage from "@/components/PropertyPage";
import SelectBox from "@/components/SelectBox";
import PropertiesFilters from "@/components/PropertiesFilters";
import NoResults from "@/components/NoResults";
import LazyImage from "@/components/LazyImage";
import RegisterYourInterestForm from "@/components/RegisterYourInterestForm";
import RangeInput from "@/components/RangeInput";
import ImportantMessageBanner from "@/components/ImportantMessageBanner";

// load modals
import PromotionModal from "@/modals/PromotionModal";
import RequestInfoModal from "@/modals/RequestInfoModal";
import RegisterInterestModal from "@/modals/RegisterInterestModal";
import ValuationSuccessModal from "@/modals/ValuationSuccessModal";

// load global styles
import "@/styles/normalize.css";
import "@/styles/typography.css";
import "@/styles/common.css";

import {
  addClassTransition,
  removeClassTransition,
} from "@/utils/classTransition";

const MODULES = [
  Anchor,
  CookieBanner,
  Header,
  SampleForm,
  ContactForm,
  NewsletterForm,
  DiscoverNewsEventsHeader,
  Sitemap,
  MainNav,
  Hero,
  NaturalCapital,
  RegisterYourInterestCTA,
  OurPropertiesSlider,
  PageHero,
  NewsAndEvents,
  Label,
  Featured,
  NewsAndEventsDetail,
  Share,
  RelatedNews,
  ArticleItem,
  ImagesGallery,
  AboutPage,
  StaticPage,
  Dots,
  Arrows,
  FaqPage,
  ContactPage,
  PeopleSlider,
  ImagePlaceholder,
  Offices,
  TitleAndAccordion,
  Error404,
  ServicesTabs,
  HomeArticlesSlider,
  BookValuationForm,
  OurPropertiesListPage,
  OurPropertiesList,
  PropertyPreview,
  PropertyPage,
  RequestInfoForm,
  SelectBox,
  PropertiesFilters,
  LazyImage,
  NoResults,
  OurPropertiesListMap,
  RegisterYourInterestForm,
  RangeInput,
  ImportantMessageBanner,
];

// modals
const MODALS = {
  promotionModal: PromotionModal,
  requestInfoModal: RequestInfoModal,
  registerInterestModal: RegisterInterestModal,
  valuationSuccessModal: ValuationSuccessModal,
};

const MAIN_MODULES = {
  // theHeader: Header,
};

export const getInstance = (el) => {
  const instanceIndex = el.getAttribute("data-instance-index");

  if (!instanceIndex) {
    return null;
  }

  return window.App.instances[parseInt(instanceIndex, 10)];
};

class App {
  lastIndex = 0;

  constructor() {
    window.addEventListener("DOMContentLoaded", this.initApp);
    window.addEventListener("resize", throttle(this.onResize, 150));
    window.addEventListener("scroll", throttle(this.onScroll, 150));
    window.addEventListener("popstate", this.onPopState);
  }

  initApp = () => {
    this.lastIndex = 0;
    this.currentView = this.getCurrentView();

    this.instances = [];
    this.modals = {};

    const main = document.querySelector("#main");
    if (main) {
      history.replaceState(
        {
          title: document.title,
          main: main.innerHTML,
          bodyClassAttribute: document.body.getAttribute("class"),
        },
        document.title,
        window.location.href,
      );
    }

    this.initArea(document);
  };

  initArea = (parentElement) => {
    if (!parentElement) {
      return [];
    }

    // create module instances
    const newInstances = MODULES.filter((module) =>
      module.hasOwnProperty("selector"),
    ).flatMap((Module) => {
      const blocks = [...parentElement.querySelectorAll(Module.selector)];
      return blocks
        .filter((block) => !block.getAttribute("data-instance-index"))
        .map((block) => {
          block.setAttribute("data-instance-index", this.lastIndex);

          this.lastIndex++;
          return new Module(block);
        });
    });

    // create modals instances
    Object.keys(MODALS).forEach((k) => {
      const Modal = MODALS[k];

      if (!Modal.hasOwnProperty("selector")) {
        return;
      }

      const block = parentElement.querySelector(Modal.selector);
      if (!block) {
        return;
      }

      if (block.getAttribute("data-instance-index")) {
        return;
      }

      block.setAttribute("data-instance-index", this.lastIndex);
      this.lastIndex++;

      this.modals[k] = new Modal(block);

      newInstances.push(this.modals[k]);
    });

    // create main module instances
    Object.keys(MAIN_MODULES).forEach((k) => {
      const Module = MAIN_MODULES[k];

      if (!Module.hasOwnProperty("selector")) {
        return;
      }

      const block = parentElement.querySelector(Module.selector);

      if (!block) {
        return;
      }

      if (block.getAttribute("data-instance-index")) {
        return;
      }

      block.setAttribute("data-instance-index", this.lastIndex);
      this.lastIndex++;

      this[k] = new Module(block);

      newInstances.push(this[k]);
    });

    // call onReady method if any
    newInstances.forEach((instance) => {
      if (instance.onReady) {
        instance.onReady();
      }
    });

    this.instances = this.instances.concat(newInstances);

    // call onComplete method if any
    newInstances.forEach((instance) => {
      if (instance.onComplete) {
        instance.onComplete();
      }
    });

    return newInstances;
  };

  destroyArea = (parentElement, keep = []) => {
    [...parentElement.children].forEach((element) => {
      // skip destroy if element is in `keep`
      if (keep.some((s) => element.matches && element.matches(s))) {
        return;
      }

      // if this element has an instance attached
      if (element.getAttribute("data-instance-index")) {
        // get instance
        const instance = getInstance(element);

        // call onDestroy method if any
        if (instance.onDestroy) {
          instance.onDestroy();
        }

        // remove instance references
        this.instances[parseInt(element.dataset.instanceIndex, 10)] = null;
        Object.keys(MAIN_MODULES).forEach((k) => {
          if (this[k] === instance) {
            this[k] = null;
          }
        });
        Object.keys(MODALS).forEach((k) => {
          if (this.modals[k] === instance) {
            this.modals[k] = null;
          }
        });
      }

      element.remove();
    });
  };

  navigateTo = async (
    url,
    replaceElement = "#main",
    destinationElement = "#main",
    scrollTop = true,
    opts = {},
  ) => {
    console.log(`Navigate to: ${url}`);

    window.App.previousUrl = window.location.href;

    const options = {
      keep: [],
      exitTransition: null,
      enterTransition: null,
      afterContentInit: null,
      ...opts,
    };

    // async request
    const response = await fetch(url, {
      credentials: "include",
    });
    const html = await response.text();

    // parse html received
    const parser = new DOMParser();
    const receivedDocument = parser.parseFromString(html, "text/html");

    // copy content from
    const from = receivedDocument.querySelector(replaceElement);
    // copy content to
    const to = document.querySelector(destinationElement);

    if (!from) {
      return;
    }

    // exit transition
    if (options.exitTransition) {
      await options.exitTransition();
    } else {
      await addClassTransition(to, "hidden", {
        awaitForProperty: "opacity",
        timeout: 1400,
      });
    }

    // destroy deleted content
    this.destroyArea(to, options.keep);

    // update history
    history.pushState(
      {
        title: receivedDocument.title,
        main: from.innerHTML,
        bodyClassAttribute: receivedDocument.body.getAttribute("class"),
      },
      receivedDocument.title,
      response.url,
    );

    // update title
    document.title = receivedDocument.title;

    // get new content
    while (from.childNodes.length > 0) {
      if (
        options.keep.some(
          (s) => from.childNodes[0].matches && from.childNodes[0].matches(s),
        )
      ) {
        // NOTE: if the head element is kept from previous content, just
        //       remove this from the new content list and go ahead in the while
        from.childNodes[0].remove();
      } else {
        // NOTE: if the head element is not kept from previous content, just
        //       detach this from the new content list and append it to
        //       destination container (`to`)
        to.appendChild(from.childNodes[0]);
      }
    }

    // Get  scripts from newly loaded content
    var scripts = to.getElementsByTagName("script");

    if (scripts.length > 0) {
      for (var i = 0; i < scripts.length; i++) {
        // Create new script
        var newScript = document.createElement("script");

        // Assign innerHTML if present
        if (scripts[i].innerHTML !== "") {
          newScript.innerHTML = scripts[i].innerHTML;
        }

        // Set attributes
        var scriptAttributes = scripts[i].attributes;

        for (var x = 0; x < scriptAttributes.length; x++) {
          newScript.setAttribute(
            scriptAttributes[x].name,
            scriptAttributes[x].value,
          );
        }
        // Remove old non-executing script nodes
        scripts[i].parentNode.removeChild(scripts[i]);
        // Append new script nodes to body
        document.body.appendChild(newScript);
      }
    }

    // get new body classes
    document.body.setAttribute(
      "class",
      receivedDocument.body.getAttribute("class"),
    );

    // enter transition
    if (options.enterTransition) {
      await options.enterTransition();
    } else {
      await removeClassTransition(to, "hidden", {
        awaitForProperty: "opacity",
        timeout: 1400,
      });
    }

    if (scrollTop) {
      // scroll top
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      });
    }

    // initialize new content
    this.initArea(to);

    if (options.afterContentInit) {
      options.afterContentInit();
    }

    if (window.gtag && window.wpData.gtagMeasurementId) {
      gtag("config", window.wpData.gtagMeasurementId, {
        page_path: window.location.pathname,
      });
    }
  };

  onResize = (e) => {
    if (!this.instances) {
      return;
    }

    const currentView = this.getCurrentView();
    const changedView = this.currentView !== currentView;

    this.currentView = currentView;

    this.instances.forEach((instance) => {
      if (instance && instance.onResize) {
        instance.onResize(e, {
          changedView,
        });
      }
    });
  };

  getCurrentView() {
    const isTablet = window.innerWidth >= tablet;
    const isLaptop = window.innerWidth >= laptop;

    if (isLaptop) {
      return "laptop";
    }

    if (isTablet) {
      return "tablet";
    }

    return "mobile";
  }

  onScroll = (e) => {
    if (!this.instances) {
      return;
    }

    this.instances.forEach((instance) => {
      if (instance && instance.onScroll) {
        instance.onScroll(e);
      }
    });
  };
  onPopState = async (e) => {
    if (e.state !== null) {
      if ("main" in e.state) {
        const fromString = e.state.main;
        const to = document.querySelector("#main");

        // exit transition
        await addClassTransition(to, "hidden", {
          awaitForProperty: "opacity",
          timeout: 1400,
        });

        // destroy deleted content
        this.destroyArea(to);

        document.title = e.state.title;

        // get new content
        to.innerHTML = fromString;

        // scroll top
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: "smooth",
        });

        // get new body classes
        document.body.setAttribute("class", e.state.bodyClassAttribute);

        // enter transition
        await removeClassTransition(to, "hidden", {
          awaitForProperty: "opacity",
          timeout: 1400,
        });

        // initialize new content
        this.initArea(to);
      }
    }
  };
}

window.App = new App();
